import { Form, Formik } from 'formik';
import React from 'react';

import { PrimaryButton, TextButton } from '@components/Button/variants';
import DropdownField from '@components/Form/DropdownField';
import FieldLabel from '@components/Form/FieldLabel';
import TextAreaField from '@components/Form/TextAreaField';
import { Env } from '@contexts/AppContext';
import { useCancelSubscription } from '@hooks';
import { cancel_offer_path } from '@routes';
import { CANCEL_REASON_OPTIONS } from '@utils/constants';
import { getImgxSrc } from '@utils/images';

import schema from './schema';

const INITIAL_VALUES: FormValues = {
  reason: { label: '', value: '' },
  notes: '',
};

interface CancelFormProps {
  env: Env;
}

interface FormValues {
  reason: {
    label: string;
    value: string;
  };
  notes: string;
}

const CancelForm = ({ env }: CancelFormProps) => {
  const { mutate: cancelSubscription } = useCancelSubscription();

  const handleSubmit = ({ reason, notes }: FormValues) => {
    cancelSubscription({
      reason: reason.value,
      notes,
    });
  };

  return (
    <Formik validationSchema={schema} initialValues={INITIAL_VALUES} onSubmit={handleSubmit}>
      {(formik) => (
        <Form onSubmit={formik.handleSubmit} id="feedback">
          <FieldLabel htmlFor="reason">Reason for cancelling?</FieldLabel>
          <DropdownField
            className="mb-24"
            name="reason"
            options={CANCEL_REASON_OPTIONS}
            placeholder="Select an option"
          />
          <FieldLabel htmlFor="notes">
            We’re very grateful for thoughts on how we can improve:
          </FieldLabel>
          <TextAreaField name="notes" placeholder="How can we do better?" rows={5} />

          <div className="my-48 border border-solid border-grayscale-beige" />
          <div className="mt-32 font-suisse-neue text-body-lg sm:mt-48">
            <p className="relative m-0 pt-32">
              <span className="absolute top-0 -translate-x-4 font-suisse-screen text-[72px] font-medium leading-none text-primary-magenta">
                “
              </span>
              I founded The Information in 2013 with a simple idea: write deeply reported articles
              about the technology industry that you won’t find elsewhere. We take this mission
              extremely seriously, and when people choose to leave us we know it is first and
              foremost because we haven’t delivered on that promise for them.
            </p>
            <br />
            <p className="m-0">
              If you have any questions, or would like to email your thoughts on why you’re
              cancelling, please feel free to email me at{' '}
              <a className="!text-primary-magenta" href="mailto:jessica@theinformation.com">
                jessica@theinformation.com
              </a>
              .”
            </p>
          </div>

          <div className="mt-32 flex gap-24">
            <div className="h-[75px] w-[75px] overflow-hidden rounded-full">
              <img
                src={getImgxSrc({
                  path: 'marketing/jessica-headshot.png',
                  isDevDependence: true,
                  envContext: env,
                })}
                alt="Jessica Lessin's HeadShot"
                className="block w-full object-cover"
              />
            </div>
            <div>
              <img
                src={getImgxSrc({
                  path: 'marketing/jessica-signature.svg',
                  isDevDependence: true,
                  envContext: env,
                })}
                alt="Jessica Lessin's Signature"
                className="block w-[154px]"
              />
              <p className="sr-only">Quote by Jessica E. Lessin</p>
              <p className="m-0 mt-24 font-suisse-intl text-[10px] font-bold leading-tight">
                CEO &amp; Founder
              </p>
            </div>
          </div>
          <div className="mt-32 flex flex-col items-center justify-between gap-16 sm:mt-48 sm:flex-row sm:gap-32">
            <PrimaryButton
              as="link"
              href={cancel_offer_path()}
              className="flex w-full items-center justify-center bg-primary-magenta px-36 py-12 font-suisse-intl text-button-md tracking-wider !text-grayscale-beige transition-colors  hover:bg-primary-pink hover:!text-primary-magenta focus:bg-primary-magenta sm:w-[336px]"
            >
              Back
            </PrimaryButton>
            <TextButton as="button" type="submit">
              Confirm cancellation
            </TextButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default CancelForm;
